import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { type Tone, toneClasses } from "./tone";
import { cn } from "@/lib/utils";

/**
 * What a pill can be tinted with: any of the tones, plus three tints that are not
 * states at all.
 *
 * `brand` is the product's own accent, worn by a chip that says where something
 * came from rather than how it is doing. `inherit` takes the colour of whatever
 * the pill sits inside, which is what a count riding inside a coloured heading
 * wants: give it a tone of its own and it stops agreeing with its label.
 * `raised` is a white chip on an info-tinted panel: the question card's count.
 *
 * All three live here rather than in `tone.ts` because neither answers "how is this
 * going", which is the question the tone vocabulary exists to answer.
 */
export type PillTone = Tone | "brand" | "inherit" | "raised";

const BRAND = "border-primary/15 bg-primary/[0.06] text-primary";
const INHERIT = "border-current/20 opacity-80";
const RAISED = "border-info-border bg-card text-foreground";

function tint(tone: PillTone): string {
  if (tone === "brand") return BRAND;
  if (tone === "inherit") return INHERIT;
  if (tone === "raised") return RAISED;
  return toneClasses(tone, "surface");
}

/**
 * Two badge sizes, where the six pills this replaced had three and the third was
 * a rounding, and the design's chip: a fixed height a row's controls line up
 * with, and room for a glyph beside words written in sentence case.
 */
const SIZES = {
  md: "px-[9px] py-[3px] text-[10px]",
  sm: "px-2 py-0.5 text-[9.5px]",
  chip: "h-[22px] gap-[5px] px-[9px] text-[11px] [&_svg]:size-[13px]",
} as const;

/**
 * A pill carries one leading mark or none, never two, and the type says so
 * rather than a comment asking for it: `dot` and `icon` occupy the same place,
 * and a caller passing both would get two decorations and no complaint.
 */
type LeadingMark =
  | { dot?: false; icon?: undefined }
  /** The filled circle before the words, for a state read at a glance down a list. */
  | { dot: true; icon?: undefined }
  /** Drawn where the dot would go, for a state that has a glyph of its own. */
  | { dot?: false; icon: ReactNode };

export type StatusPillProps = ComponentPropsWithoutRef<"span"> & {
  tone: PillTone;
  size?: keyof typeof SIZES;
  /** Sentence case, for a chip carrying a sentence rather than a state. */
  plain?: boolean;
  children: ReactNode;
} & LeadingMark;

/**
 * The pill's own look, for a control that must read as one: a menu whose
 * trigger is the state it changes, as a claim's category tag is.
 */
export function pillClasses(
  tone: PillTone,
  size: keyof typeof SIZES = "md",
  plain = false,
): string {
  // A state is a squared tag, as the design draws every one; a sentence keeps
  // the round end that says it is words, not a state.
  return cn(
    "inline-flex flex-none items-center gap-1.5 whitespace-nowrap border",
    plain
      ? "rounded-full font-semibold"
      : "rounded-[5px] font-bold uppercase tracking-wide",
    SIZES[size],
    tint(tone),
  );
}

/**
 * The chip a row wears to say how it is doing.
 *
 * Six of these were written separately, at three font sizes and three vertical
 * paddings, and every one of them spelled its own tint. Colour is never the only
 * carrier here: the state is always in the words as well, so the pill reads the
 * same to someone who cannot tell the tints apart.
 *
 * Anything not passed is forwarded to the span, which is how each call site keeps
 * the `data-testid` and `data-*` attributes its own tests already key off.
 */
export function StatusPill({
  tone,
  size = "md",
  plain = false,
  dot,
  icon,
  children,
  className,
  ...rest
}: StatusPillProps) {
  return (
    <span className={cn(pillClasses(tone, size, plain), className)} {...rest}>
      {dot === true && (
        <span
          aria-hidden
          className="size-1.5 rounded-full bg-current opacity-70"
        />
      )}
      {icon}
      {children}
    </span>
  );
}
